import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  limit,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { voucherProgramService } from '@/services/voucherProgramService'

function toMoney(n) {
  return Number(n || 0)
}

export const voucherService = {
  async listCustomerVouchers(customerId, max = 30) {
    const q = query(
      collection(db, 'vouchers'),
      where('customerId', '==', customerId),
      orderBy('issuedAt', 'desc'),
      limit(max)
    )
    const snap = await getDocs(q)
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
  },

  async listCustomerVouchersByCustomerIds(customerIds = [], max = 50) {
    const ids = Array.from(new Set((customerIds || []).filter(Boolean)))
    if (ids.length === 0) return []

    const chunks = []
    for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10))

    const all = []
    for (const chunk of chunks) {
      const q = query(
        collection(db, 'vouchers'),
        where('customerId', 'in', chunk),
        orderBy('issuedAt', 'desc'),
        limit(max)
      )
      const snap = await getDocs(q)
      all.push(...snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    }

    const map = new Map()
    for (const v of all) map.set(v.id, v)
    return Array.from(map.values()).sort((a, b) => {
      const da = a?.issuedAt?.toDate?.() ? a.issuedAt.toDate().getTime() : new Date(a?.issuedAt || 0).getTime()
      const db = b?.issuedAt?.toDate?.() ? b.issuedAt.toDate().getTime() : new Date(b?.issuedAt || 0).getTime()
      return db - da
    })
  },

  async listVouchers(filters = {}, max = 100) {
    const constraints = []
    if (filters.status) constraints.push(where('status', '==', filters.status))
    if (filters.branchId) constraints.push(where('branchId', '==', filters.branchId))
    const q = query(collection(db, 'vouchers'), ...constraints, orderBy('issuedAt', 'desc'), limit(max))
    const snap = await getDocs(q)
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
  },

  async applySpendAndIssueVouchers({ customerId, customerName, branchId, amount, sourceRef }) {
    const program = await voucherProgramService.get()
    if (!program.enabled) return { issued: 0, progress: 0, threshold: program.spendThreshold }

    const spendThreshold = toMoney(program.spendThreshold)
    const rewardAmount = toMoney(program.rewardAmount)
    if (!spendThreshold || spendThreshold <= 0) return { issued: 0, progress: 0, threshold: spendThreshold }

    const customerRef = doc(db, 'customers', customerId)

    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(customerRef)
      if (!snap.exists()) return { issued: 0, progress: 0, threshold: spendThreshold }

      const c = snap.data()
      const prevProgress = Math.max(0, toMoney(c.voucherSpendProgress))
      const nextProgress = Math.max(0, prevProgress + toMoney(amount))

      let remainingProgress = nextProgress
      let issued = 0
      let issuedTotalValue = 0
      const rewardKind = program.rewardKind || program.rewardType || 'amount'
      const rewardCatalogServiceId = program.rewardCatalogServiceId || null
      const rewardCatalogTierId = program.rewardCatalogTierId || null

      while (remainingProgress >= spendThreshold) {
        remainingProgress = program.carryOverRemainder ? remainingProgress - spendThreshold : 0
        issued += 1
        if (rewardKind === 'amount') issuedTotalValue += rewardAmount

        await addDoc(collection(db, 'vouchers'), {
          customerId,
          customerName: customerName || c.name || '',
          branchId: branchId || c.branchId || null,
          status: 'active',
          value: rewardKind === 'amount' ? rewardAmount : 0,
          rewardType: program.rewardType,
          rewardKind,
          rewardLabel: program.rewardLabel || 'Voucher',
          catalogServiceId: rewardKind === 'service' ? rewardCatalogServiceId : null,
          catalogTierId: rewardKind === 'service' ? rewardCatalogTierId : null,
          program: {
            spendThreshold,
            rewardAmount,
            rewardType: program.rewardType,
            rewardKind,
            rewardCatalogServiceId,
            rewardCatalogTierId,
          },
          issuedAt: serverTimestamp(),
          sourceRef: sourceRef || null,
          redeemedAt: null,
          redeemedByUid: null,
          redeemedTransactionId: null,
          redeemedWashId: null,
        })
      }

      const prevBalance = Math.max(0, toMoney(c.voucherBalance))
      tx.update(customerRef, {
        voucherSpendProgress: remainingProgress,
        voucherBalance: prevBalance + issuedTotalValue,
        vouchersIssuedCount: toMoney(c.vouchersIssuedCount) + issued,
        updatedAt: serverTimestamp(),
      })

      return { issued, progress: remainingProgress, threshold: spendThreshold, program }
    })
  },

  async redeemVoucher({ voucherId, redeemedByUid, redeemedTransactionId, redeemedWashId }) {
    const ref = doc(db, 'vouchers', voucherId)
    await updateDoc(ref, {
      status: 'redeemed',
      redeemedAt: serverTimestamp(),
      redeemedByUid: redeemedByUid || null,
      redeemedTransactionId: redeemedTransactionId || null,
      redeemedWashId: redeemedWashId || null,
    })
  },
}

